import type { KeyboardEvent, ReactNode } from 'react'
import type { Treatment } from '@/types/clinic'

import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { Check, Pencil, X } from 'lucide-react'
import { roundMoney } from '@/lib/format'
import { useMoney } from '@/hooks/use-clinic-helpers'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface PriceCellProps {
    treatment: Treatment
    onSave: (price: number) => void
}

// Click the price to edit it in place. Enter saves, Escape cancels.
const PriceCell = ({ treatment, onSave }: PriceCellProps): ReactNode => {
    const money = useMoney()
    const [editing, setEditing] = useState(false)
    const [value, setValue] = useState(String(treatment.price))
    const inputRef = useRef<HTMLInputElement>(null)

    const start = () => {
        setValue(String(treatment.price))
        setEditing(true)
        window.setTimeout(() => inputRef.current?.select(), 0)
    }

    const save = () => {
        const next = Number(value)
        if (value === '' || Number.isNaN(next) || next < 0) {
            toast.error('Enter a valid price')
            return
        }
        setEditing(false)
        if (roundMoney(next) !== treatment.price) onSave(roundMoney(next))
    }

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            save()
        }
        if (event.key === 'Escape') setEditing(false)
    }

    if (editing) {
        return (
            <div className='flex items-center justify-end gap-1'>
                <Input
                    ref={inputRef}
                    type='number'
                    inputMode='decimal'
                    min={0}
                    value={value}
                    onChange={event => setValue(event.target.value)}
                    onKeyDown={onKeyDown}
                    className='h-8 w-28 text-right'
                    aria-label={`Price for ${treatment.name}`}
                />
                <Button size='icon-sm' variant='ghost' onClick={save} aria-label='Save price'>
                    <Check aria-hidden='true' />
                </Button>
                <Button size='icon-sm' variant='ghost' onClick={() => setEditing(false)} aria-label='Cancel'>
                    <X aria-hidden='true' />
                </Button>
            </div>
        )
    }

    return (
        <button
            type='button'
            onClick={start}
            className='group inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-semibold text-foreground tabular-nums transition-colors hover:bg-accent focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
            aria-label={`Edit price for ${treatment.name}, currently ${money(treatment.price)}`}
        >
            {money(treatment.price)}
            <Pencil className='size-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100' aria-hidden='true' />
        </button>
    )
}

export default PriceCell
